import React from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiShield, FiTarget, FiSettings, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { useEffect } from 'react';

export default function Cookies() {
  useEffect(() => {
    trackEvent('page_view', 'navigation', 'cookie_policy');
  }, []);

  return (
    <>
      <SEO
        title="Cookie Policy - Bailebelle"
        description="Learn about how Bailebelle uses cookies to enhance your browsing experience and improve our services."
        keywords="cookie policy, cookies, privacy, tracking, data protection"
        url="/cookies"
      />
    <div className="min-h-screen bg-black text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
          Cookie Policy
        </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Learn about how Bailebelle uses cookies and similar technologies to enhance your shopping experience.
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiPackage className="text-pink-500 mt-1" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">What Are Cookies?</h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Cookies are small text files that are placed on your device when you visit our website. 
                  They help us provide you with a better shopping experience by remembering your preferences, 
                  analyzing site traffic, and personalizing content. We use cookies responsibly and in 
                  compliance with applicable privacy laws, including GDPR and CCPA.
                </p>
              </div>
            </div>
          </div>

          {/* Cookie Categories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Types of Cookies We Use</h2>
              
              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiSettings className="text-blue-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Essential Cookies</h3>
                    <p className="text-gray-400 mb-3">
                      These cookies are necessary for the website to function properly. They enable core functionality 
                      such as security, network management, and accessibility.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Cookie consent preferences</li>
                      <li>• Shopping cart functionality</li>
                      <li>• Website security and authentication</li>
                    </ul>
                    <p className="text-blue-400 text-xs mt-3 font-semibold">
                      These cookies cannot be disabled as they are essential for site functionality.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiTarget className="text-green-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Analytics Cookies</h3>
                    <p className="text-gray-400 mb-3">
                      These cookies help us understand how visitors interact with our website by collecting 
                      and reporting information anonymously.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Google Analytics (GA4)</li>
                      <li>• Microsoft Clarity</li>
                      <li>• Page performance metrics</li>
                    </ul>
                    <p className="text-green-400 text-xs mt-3 font-semibold">
                      You can manage these through our cookie consent banner.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <FiShield className="text-pink-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Marketing Cookies</h3>
                    <p className="text-gray-400 mb-3">
                      These cookies are used to deliver advertisements and track campaign performance. 
                      They help us show you relevant products and measure advertising effectiveness.
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li>• Google Tag Manager (GTM)</li>
                      <li>• Social media integration</li>
                      <li>• Behavioral advertising</li>
                    </ul>
                    <p className="text-pink-400 text-xs mt-3 font-semibold">
                      You can opt out of these cookies at any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* How We Use Cookies */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">How We Use Cookies</h2>
              
              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Website Functionality</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Remember your preferences and settings</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Keep your shopping cart items saved</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Maintain your session while browsing</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Enable secure payment processing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Analytics & Performance</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Understand how you use our website</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Identify popular products and pages</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Improve website performance and speed</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Fix bugs and optimize user experience</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Personalization & Marketing</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Show you personalized product recommendations</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Remember your product preferences</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Deliver relevant advertisements</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Measure ad campaign effectiveness</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Managing Cookies */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Managing Your Cookie Preferences</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiSettings className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Cookie Consent Banner</h3>
                <p className="text-gray-400 text-sm">
                  When you first visit our site, you'll see a cookie consent banner. 
                  You can customize which cookies to accept.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiRefreshCw className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Change Preferences</h3>
                <p className="text-gray-400 text-sm">
                  You can update your cookie preferences at any time by clicking the 
                  "Cookie Settings" link in our footer.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiX className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Browser Settings</h3>
                <p className="text-gray-400 text-sm">
                  You can also manage cookies through your browser settings. Note that 
                  blocking cookies may affect website functionality.
                </p>
              </div>
            </div>
          </div>

          {/* Third-Party Cookies */}
          <div className="bg-gray-900 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Third-Party Cookies</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Service Providers</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Google Analytics:</strong> Website analytics and performance tracking</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Microsoft Clarity:</strong> User behavior analysis and heatmaps</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Google Tag Manager:</strong> Tag and event management</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Social Media</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Social sharing buttons for Instagram, Facebook, Pinterest</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Social media login and authentication</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Embedded social media content and widgets</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiShield className="text-blue-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Your Privacy Rights</h3>
                  <p className="text-gray-400 text-sm">
                    Under GDPR, CCPA, and other privacy laws, you have the right to access, 
                    modify, or delete your personal data. You can also opt out of cookie-based 
                    tracking at any time.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-pink-900/30 to-purple-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiShield className="text-pink-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Data Security</h3>
                  <p className="text-gray-400 text-sm">
                    We use industry-standard encryption and security measures to protect 
                    your data. Cookie data is stored securely and only used for the purposes 
                    stated in this policy.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Questions About Our Cookie Policy?</h2>
              <p className="text-gray-400 mb-6">
                If you have any questions about how we use cookies or would like more information 
                about our data practices, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Contact Us
                </Link>
                <Link
                  to="/privacy"
                  className="inline-flex items-center px-6 py-3 border-2 border-gray-600 text-white font-semibold rounded-lg hover:border-pink-500 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
              <p className="text-gray-500 text-sm">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
